import { Component, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CreateCustomExemptionParkingTypeRequest, ParkingTypeService, ParkerService, LicensePlateDto, ParkingSpaceDto, ParkingZoneDto } from '../../../../../api/gen';

export interface LicensePlateItem {
  id: string;
  fullNumber: string;
}

export interface CustomExemptionResult {
  success: boolean;
  data?: any;
}

@Component({
  selector: 'app-add-custom-exemption',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatChipsModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatSnackBarModule
  ],
  providers: [provideNativeDateAdapter()],
  templateUrl: './add-custom-exemption.component.html',
  styleUrl: './add-custom-exemption.component.scss'
})
export class AddCustomExemptionComponent implements OnInit {
  private fb = inject(FormBuilder);
  private parkingTypeService = inject(ParkingTypeService);
  private parkerService = inject(ParkerService);
  private snackBar = inject(MatSnackBar);
  @Input({ required: true }) parkingOperatorId: string | undefined;

  @Input() parkingSpace!: ParkingSpaceDto;
  @Output() submitted = new EventEmitter<CustomExemptionResult>();

  form!: FormGroup;
  isLoading = signal(false);
  isAddingPlate = signal(false);
  licensePlates = signal<LicensePlateItem[]>([]);
  licensePlateInput = signal('');
  availableZones = signal<ParkingZoneDto[]>([]);

  ngOnInit(): void {
    this.initForm();
    this.availableZones.set(this.parkingSpace?.parkingZones ?? []);
  }

  private initForm(): void {
    this.form = this.fb.group({
      licensePlateInput: [''],
      availableForParkingZones: [[] as string[]],
      availableFrom: [null],
      availableTo: [null],
      skipProcessRecording: [false]
    });
  }

  addLicensePlate(): void {
    const value = this.form.get('licensePlateInput')?.value?.trim().toUpperCase();
    if (!value) {
      this.snackBar.open('Please enter a license plate number', 'Close', { duration: 3000 });
      return;
    }

    // Check if already added
    if (this.licensePlates().some(p => p.fullNumber === value)) {
      this.snackBar.open('License plate already added', 'Close', { duration: 3000 });
      return;
    }

    this.isAddingPlate.set(true);

    // Register or fetch license plate using parkerRegisterLicensePlatePost
    this.parkerService.parkerRegisterLicensePlatePost({ fullNumber: value }).subscribe({
      next: (result: LicensePlateDto) => {
        if (result.id) {
          this.licensePlates.update(plates => [...plates, { id: result.id!, fullNumber: result.fullNumber || value }]);
          this.form.get('licensePlateInput')?.setValue('');
          this.snackBar.open('License plate added successfully', 'Close', { duration: 2000 });
        } else {
          this.snackBar.open('Failed to get license plate ID', 'Close', { duration: 3000 });
        }
        this.isAddingPlate.set(false);
      },
      error: (error) => {
        console.error('Error registering license plate:', error);
        this.snackBar.open('Failed to register license plate', 'Close', { duration: 3000 });
        this.isAddingPlate.set(false);
      }
    });
  }

  removeLicensePlate(plate: LicensePlateItem): void {
    this.licensePlates.update(plates => plates.filter(p => p.id !== plate.id));
  }

  submit(): void {
    if (this.licensePlates().length === 0) {
      this.snackBar.open('Please add at least one license plate', 'Close', { duration: 3000 });
      return;
    }

    this.isLoading.set(true);

    const formValue = this.form.value;
    const licensePlateIds = this.licensePlates().map(p => p.id);

    const request: CreateCustomExemptionParkingTypeRequest = {
      licensePlates: licensePlateIds,
      availableFrom: formValue.availableFrom ? new Date(formValue.availableFrom).toISOString() : null,
      availableTo: formValue.availableTo ? new Date(formValue.availableTo).toISOString() : null,
      availableForParkingSpaces: this.parkingSpace?.id ? [this.parkingSpace.id] : null,
      // Empty means the whole space; sent as an empty list, like the short-term form.
      availableForParkingZones: formValue.availableForParkingZones ?? [],
      organizationId: null,
      skipProcessRecording: formValue.skipProcessRecording ?? false
    };

    this.parkingTypeService.parkingTypesCustomExemptionsPost(this.parkingOperatorId, request).subscribe({
      next: (result) => {
        this.snackBar.open('Custom exemption created successfully', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.submitted.emit({ success: true, data: result });
      },
      error: (error) => {
        console.error('Error creating custom exemption:', error);
        this.snackBar.open('Failed to create custom exemption', 'Close', { duration: 3000 });
        this.isLoading.set(false);
        this.submitted.emit({ success: false });
      }
    });
  }
}
